import React from "react";
import { MapPin } from "lucide-react";
import { IMG } from "../lib/constants";

// 4 featured places with images
const FEATURED = [
    {
        name: "Railway Scenic Bridge",
        image: "https://commons.wikimedia.org/wiki/Special:FilePath/Train_enroute_green_route.JPG?width=1280",
        day: "Day 1",
        km: "2 km",
    },
    {
        name: "Manjarabad Star Fort",
        image: "https://customer-assets.emergentagent.com/job_veeras-resorts/artifacts/7gcte08k_munjrabad.jfif",
        day: "Day 1",
        km: "4 km",
    },
    {
        name: "Chennakeshava Hoysala Temple, Belur",
        image: "https://commons.wikimedia.org/wiki/Special:FilePath/Chennakesava_Temple,_Belur_03.jpg?width=1280",
        day: "Day 1",
        km: "30 km",
    },
    {
        name: "Magajahalli Waterfalls",
        image: "https://customer-assets.emergentagent.com/job_veeras-resorts/artifacts/kuw5y06h_magajahalli%20falls.jfif",
        day: "Day 2",
        km: "18 km",
    },
];

const DAYS = [
    {
        num: "01",
        label: "Day One",
        title: "Temples & Forts",
        places: [
            { name: "Sakleshpura Temple", km: "2 km" },
            { name: "Railway Scenic Bridge", km: "2 km" },
            { name: "Sakleshpura Fish Lake", km: "2 km" },
            { name: "Manjarabad Star Fort", km: "4 km" },
            { name: "Yagachi Water Sports & Dam", km: "32 km" },
            { name: "Chennakeshava Hoysala Temple, Belur — UNESCO approved", km: "30 km" },
            { name: "Shettyhalli Floating Church Shooting Point", km: "30 km" },
        ],
    },
    {
        num: "02",
        label: "Day Two",
        title: "Falls & Tea Estates",
        places: [
            { name: "Magajahalli (Hanbal) Waterfalls", km: "18 km" },
            { name: "Kadumane Tea Plantation", km: "20 km", sub: "11,000 acres of boundary" },
            { name: "Bettada Byraveshwara Heritage Temple & Pandavara Betta", km: "27 km" },
            { name: "Ettinabhuja Trekking and Agni Gudda Hills", km: "30 km" },
        ],
    },
    {
        num: "03",
        label: "Day Three",
        title: "Off-Road & Pilgrimage",
        places: [
            { name: "Mukanamane Waterfalls", km: "3 km" },
            { name: "Hosahalli Betta Shooting Point", km: "32 km" },
            { name: "Gavi Betta Off-road", km: "35 km" },
            { name: "Patla Betta View Point", km: "38 km" },
            { name: "Mallalli Waterfalls", km: "40 km" },
            { name: "Bisle View Point", km: "45 km" },
            { name: "Kukke Subramanya Temple", km: "60 km" },
            { name: "Dharmasthala Temple", km: "80 km" },
        ],
    },
];

export default function Nearby() {
    return (
        <section id="nearby" className="relative py-24 md:py-32 bg-white" data-testid="nearby-section">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
                    <div className="lg:col-span-7">
                        <p className="text-xs tracking-[0.3em] uppercase text-forest-700 mb-5">Things to see</p>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-coffee-900 leading-tight">
                            Places to visit in <span className="italic text-forest-700">Sakleshpura</span> — from our property.
                        </h2>
                    </div>
                    <p className="lg:col-span-4 lg:col-start-9 text-stone-600 self-end leading-relaxed">
                        Three handcrafted route plans — from nearby temples and waterfalls to forts, tea estates and off-road viewpoints. Distances measured from Veeras.
                    </p>
                </div>

                {/* 4 featured places */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-16">
                    {FEATURED.map((p, i) => (
                        <article
                            key={p.name}
                            className="group relative h-72 md:h-80 rounded-2xl overflow-hidden"
                            data-testid={`nearby-feature-${i}`}
                        >
                            <img
                                src={p.image}
                                alt={p.name}
                                className="absolute inset-0 h-full w-full object-cover group-hover:scale-110 transition-transform duration-[1200ms]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-coffee-900 via-coffee-900/30 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                                <span className="text-[10px] tracking-[0.25em] uppercase text-[#E8B769]">
                                    {p.day} · {p.km}
                                </span>
                                <h3 className="font-serif text-cream text-xl md:text-2xl mt-1">{p.name}</h3>
                            </div>
                        </article>
                    ))}
                </div>

                {/* 3-column day plan */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                    {DAYS.map((d) => (
                        <div
                            key={d.num}
                            className="relative bg-cream border border-sand rounded-2xl p-7 md:p-8 flex flex-col"
                            data-testid={`day-card-${d.num}`}
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-forest-700 text-cream font-serif text-xl">
                                    {d.num}
                                </span>
                                <div>
                                    <p className="text-[10px] tracking-[0.3em] uppercase text-forest-700">{d.label}</p>
                                    <h3 className="font-serif text-2xl text-coffee-900 leading-tight">{d.title}</h3>
                                </div>
                            </div>
                            <ul className="space-y-3.5 flex-1">
                                {d.places.map((place) => (
                                    <li key={place.name} className="flex items-start gap-3">
                                        <MapPin className="h-4 w-4 text-forest-700 shrink-0 mt-1" />
                                        <div className="flex-1 flex items-baseline justify-between gap-3">
                                            <span className="text-coffee-900 text-sm md:text-base">
                                                {place.name}
                                                {place.sub && (
                                                    <span className="block text-[11px] text-stone-500 mt-0.5">{place.sub}</span>
                                                )}
                                            </span>
                                            <span className="text-xs tracking-wide text-coffee-600 font-medium whitespace-nowrap">
                                                {place.km}
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <p className="mt-8 text-center text-coffee-600 text-sm italic">
                    All distances measured from Veeras Resorts. We can arrange cars and bikes for your route — just ask us at reception.
                </p>
            </div>
        </section>
    );
}
